import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Calendar, Download } from "lucide-react";
import * as XLSX from "xlsx";

interface BilledAppointment {
  id: string;
  appointment_date: string;
  appointment_time: string;
  patient_name: string;
  service_name: string;
  service_type: string | null;
  amount: number;
  invoice_number: string;
}

export const AppointmentsTable: React.FC = () => {
  const [appointments, setAppointments] = useState<BilledAppointment[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadBilledAppointments();
  }, []);

  const loadBilledAppointments = async () => {
    setLoading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      // Load invoices with related appointments
      const { data: invoicesData, error: invoicesError } = await supabase
        .from("invoices")
        .select(
          `
          id,
          invoice_number,
          invoice_date,
          amount,
          patient:patients(id, name),
          service:services(name)
        `,
        )
        .eq("user_id", user.id)
        .order("invoice_date", { ascending: false });

      if (invoicesError) throw invoicesError;

      // Load appointments for these patients
      const patientIds =
        invoicesData
          ?.map((inv) => (Array.isArray(inv.patient) ? inv.patient[0]?.id : inv.patient?.id))
          .filter(Boolean) || [];

      const { data: appointmentsData, error: appointmentsError } = await supabase
        .from("appointments")
        .select("*")
        .in("patient_id", patientIds)
        .eq("practitioner_id", user.id)
        .order("appointment_date", { ascending: false });

      if (appointmentsError) throw appointmentsError;

      // Match invoices with appointments by patient and date proximity
      const billedAppointments: BilledAppointment[] = [];

      invoicesData?.forEach((invoice) => {
        const patient = Array.isArray(invoice.patient) ? invoice.patient[0] : invoice.patient;
        const service = Array.isArray(invoice.service) ? invoice.service[0] : invoice.service;

        // Find appointment on or near invoice date
        const appointment = appointmentsData?.find(
          (apt) =>
            apt.patient_id === patient?.id &&
            Math.abs(new Date(apt.appointment_date).getTime() - new Date(invoice.invoice_date).getTime()) <
              7 * 24 * 60 * 60 * 1000, // Within 7 days
        );

        if (appointment && patient && service) {
          billedAppointments.push({
            id: invoice.id,
            appointment_date: appointment.appointment_date,
            appointment_time: appointment.appointment_time || "",
            patient_name: patient.name || "N/A",
            service_name: service.name || "N/A",
            service_type: appointment.service_type,
            amount: invoice.amount,
            invoice_number: invoice.invoice_number,
          });
        }
      });

      setAppointments(billedAppointments);
    } catch (error: any) {
      console.error("Erreur chargement rendez-vous facturés:", error);
      toast.error("Erreur lors du chargement des rendez-vous");
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    const data = appointments.map((apt) => ({
      "N° Facture": apt.invoice_number,
      Date: new Date(apt.appointment_date).toLocaleDateString("fr-FR"),
      Heure: apt.appointment_time,
      Patient: apt.patient_name,
      Service: apt.service_name,
      Type: apt.service_type || "Standard",
      "Montant (€)": apt.amount.toFixed(2),
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Rendez-vous facturés");

    // Auto-size columns
    const maxWidth = 30;
    const colWidths = Object.keys(data[0] || {}).map((key) => ({
      wch: Math.min(maxWidth, Math.max(key.length, ...data.map((row) => String(row[key as keyof typeof row]).length))),
    }));
    ws["!cols"] = colWidths;

    XLSX.writeFile(wb, `rendez-vous-factures-${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Export Excel réussi");
  };

  return (
    <Card className="shadow-card bg-gradient-card border-0">
      <CardHeader className="border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-2xl font-playfair text-primary flex items-center gap-2">
              <Calendar className="w-6 h-6" />
              Historique des factures
            </CardTitle>
            <CardDescription>Tableau récapitulatif de toutes vos factures</CardDescription>
          </div>
          <Button onClick={exportToExcel} disabled={appointments.length === 0} className="gap-2">
            <Download className="w-4 h-4" />
            Exporter Excel
          </Button>
        </div>
      </CardHeader>

      <CardContent className="pt-6">
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Chargement des rendez-vous...</div>
        ) : appointments.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">Aucun rendez-vous facturé trouvé</div>
        ) : (
          <>
            <div className="rounded-lg border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Heure</TableHead>
                    <TableHead>Consultant</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Montant</TableHead>
                    <TableHead>N° Facture</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {appointments.map((apt) => (
                    <TableRow key={apt.id}>
                      <TableCell>{new Date(apt.appointment_date).toLocaleDateString("fr-FR")}</TableCell>
                      <TableCell>{apt.appointment_time}</TableCell>
                      <TableCell className="font-medium">{apt.patient_name}</TableCell>
                      <TableCell>{apt.service_name}</TableCell>
                      <TableCell className="text-muted-foreground">{apt.service_type || "Standard"}</TableCell>
                      <TableCell className="text-right font-semibold">{apt.amount.toFixed(2)}€</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{apt.invoice_number}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="mt-4 text-sm text-muted-foreground text-right">
              Total: {appointments.length} rendez-vous -{" "}
              {appointments.reduce((sum, apt) => sum + apt.amount, 0).toFixed(2)}€
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
